import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus, Search, Pencil, Trash2, Package, Sliders, RotateCcw, Save as SaveIcon,
  Layers, AlertTriangle, CheckCircle2, Calculator, X,
} from "lucide-react";
import { FENCE_LABELS, fmtCurrencyExact } from "@/lib/constants";
import { FencePhotoManager } from "@/components/FencePhotoManager";

const EMPTY = {
  name: "", sku: "", category: "material", fence_types: [], unit: "ea",
  unit_price: 0, supplier_id: "none", description: "",
  pack_size: null, pack_unit: "", waste_pct: null,
  is_composite: false, composite_bom: [], composite_cost_override: null,
  needs_cost_review: false,
};
const UNITS = ["ea", "lm", "sqm", "hr", "day", "kg", "m3"];

const TAB_DEFS = [
  { key: "all", label: "All", testid: "tab-all" },
  { key: "material", label: "Raw Materials", testid: "tab-material" },
  { key: "composite", label: "Composites (Bundles)", testid: "tab-composite" },
  { key: "labour", label: "Labour", testid: "tab-labour" },
  { key: "incidental", label: "Incidentals", testid: "tab-incidental" },
  { key: "review", label: "Needs Review", testid: "tab-review" },
];

const RATIO_FIELDS = [
  { key: "posts_per_metre", label: "Posts per metre", hint: "Extra posts CCFI adds beyond one-per-panel. Default 0.5.", step: 0.05 },
  { key: "concrete_bags_per_post", label: "Concrete bags / post", hint: "20 kg post-mix bags per post footing.", step: 0.5 },
  { key: "rail_brackets_per_post", label: "Rail brackets / post", hint: "Bracket count for the take-off.", step: 1 },
  { key: "rails_per_panel_std", label: "Rails per panel (≤1.8 m)", hint: "Standard-height panels.", step: 1 },
  { key: "rails_per_panel_tall", label: "Rails per panel (>1.8 m)", hint: "Tall panels.", step: 1 },
  { key: "panel_width_m", label: "Panel width (m)", hint: "Stock panel/sheet width.", step: 0.1 },
  { key: "stock_length_m", label: "Stock bar length (m)", hint: "Rail / frame bar length for cut planning.", step: 0.1 },
  { key: "slat_height_mm", label: "Slat height (mm)", hint: "Aluminium slat blade height.", step: 5 },
  { key: "slat_gap_mm", label: "Slat gap (mm)", hint: "Air gap between slats.", step: 1 },
  { key: "chain_wire_roll_m", label: "Chain-wire roll (m)", hint: "Roll length used by suppliers.", step: 1 },
  { key: "picket_gap_mm", label: "Pool picket gap (mm)", hint: "AS 1926.1 pool fence gap.", step: 1 },
];

function PricingRatiosCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["pricing-ratios"],
    queryFn: async () => (await api.get("/settings/pricing-ratios")).data,
  });
  const [draft, setDraft] = useState(null);
  const ratios = draft ?? data?.ratios;
  const defaults = data?.defaults;
  const dirty = draft && data?.ratios &&
    Object.keys(draft).some((k) => Number(draft[k]) !== Number(data.ratios[k]));

  const save = useMutation({
    mutationFn: async () => (await api.put("/settings/pricing-ratios", draft)).data,
    onSuccess: (fresh) => {
      qc.setQueryData(["pricing-ratios"], (d) => ({ ...(d || {}), ratios: fresh }));
      setDraft(null);
      toast.success("Pricing defaults saved — new take-offs will use these values.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post("/settings/pricing-ratios/reset")).data,
    onSuccess: (fresh) => {
      qc.setQueryData(["pricing-ratios"], (d) => ({ ...(d || {}), ratios: fresh }));
      setDraft(null);
      toast.success("Reverted to CCFI industry defaults.");
    },
  });

  if (isLoading || !ratios) return null;

  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow mb-4" data-testid="pricing-ratios-card">
      <div className="p-3 sm:p-4 border-b border-slate-200 bg-slate-50/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start sm:items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 shrink-0 bg-amber-100 text-amber-700 flex items-center justify-center rounded-sm">
            <Sliders size={14} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-sm sm:text-base font-semibold text-slate-900">Line-item pricing defaults</div>
            <div className="text-[11px] sm:text-xs text-slate-500 leading-tight">Trent's posts-per-metre &amp; concrete ratios — used by every take-off &amp; quote.</div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm h-9 flex-1 sm:flex-none"
            onClick={() => {
              if (window.confirm("Reset all ratios to CCFI industry defaults?")) reset.mutate();
            }}
            data-testid="reset-ratios-btn"
            disabled={reset.isPending}
          >
            <RotateCcw size={12} className="mr-1.5" /> Reset
          </Button>
          <Button
            size="sm"
            className="rounded-sm h-9 flex-1 sm:flex-none bg-cyan-600 hover:bg-cyan-700 text-white"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            data-testid="save-ratios-btn"
          >
            <SaveIcon size={12} className="mr-1.5" /> {dirty ? "Save" : "Saved"}
          </Button>
        </div>
      </div>
      <div className="p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2.5 sm:gap-3">
        {RATIO_FIELDS.map((f) => {
          const current = ratios[f.key];
          const def = defaults?.[f.key];
          const isDefault = Number(current) === Number(def);
          return (
            <div key={f.key}>
              <div className="flex items-center gap-1.5">
                <label htmlFor={`ratio-${f.key}`} className="stamp-label text-[9px] sm:text-[10px] text-slate-500 leading-tight">{f.label}</label>
                {!isDefault && <Badge variant="outline" className="rounded-sm text-[9px] border-amber-300 bg-amber-50 text-amber-700 py-0 h-4">Custom</Badge>}
              </div>
              <Input
                id={`ratio-${f.key}`}
                data-testid={`ratio-${f.key}`}
                type="number"
                inputMode="decimal"
                step={f.step}
                value={current}
                onChange={(e) => setDraft({ ...ratios, [f.key]: e.target.value === "" ? 0 : Number(e.target.value) })}
                className="rounded-sm h-10 sm:h-9 mt-0.5 font-mono text-sm"
              />
              <div className="hidden sm:block text-[10px] text-slate-400 mt-0.5 leading-tight">{f.hint}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// BOM editor — shown only when a material is flagged is_composite.
// Each row references another material by id + qty_per_unit. The parent's
// per-unit cost is derived from ∑(component.cost × qty_per_unit) unless the
// composite_cost_override is set (manual pin).
// -----------------------------------------------------------------------------
function BomEditor({ editing, setEditing, materials }) {
  const bom = useMemo(() => editing.composite_bom || [], [editing.composite_bom]);
  const override = editing.composite_cost_override;

  // Live cost preview (client-side; server-side is the source of truth)
  const componentIndex = useMemo(() => {
    const map = {};
    for (const m of materials || []) map[m.id] = m;
    return map;
  }, [materials]);

  const derivedCost = useMemo(() => {
    let t = 0;
    for (const row of bom) {
      const comp = componentIndex[row.material_id];
      if (!comp) continue;
      const qty = Number(row.qty_per_unit || 0);
      const raw = Number(comp.unit_price || 0);
      // Mirror the backend inference: rows saved before the pack-mode UI have
      // qty_mode=null — the cost engine treats those as "pack" when the raw
      // has a pack_size, so the preview must too.
      const mode = row.qty_mode === "pack" || row.qty_mode === "unit"
        ? row.qty_mode
        : (Number(comp.pack_size) > 0 ? "pack" : "unit");
      // iter-63: mirror the backend conversion so the live preview matches
      // what /materials/:id/cost will return once saved.
      let effUnitCost = raw;
      if (mode === "pack" && Number(comp.pack_size) > 0) {
        effUnitCost = raw / Number(comp.pack_size);
      }
      let line = qty * effUnitCost;
      if (Number(comp.waste_pct) > 0) {
        line *= 1 + Number(comp.waste_pct) / 100;
      }
      t += line;
    }
    return t;
  }, [bom, componentIndex]);

  const effectiveCost = override != null && override !== ""
    ? Number(override)
    : bom.length > 0
      ? derivedCost
      : Number(editing.unit_price || 0);

  const addRow = () => {
    setEditing({
      ...editing,
      composite_bom: [...bom, { material_id: "", qty_per_unit: 1, notes: "" }],
    });
  };
  const updateRow = (idx, patch) => {
    const next = bom.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setEditing({ ...editing, composite_bom: next });
  };
  const removeRow = (idx) => {
    setEditing({ ...editing, composite_bom: bom.filter((_, i) => i !== idx) });
  };

  // Only allow raw materials as BOM components — filter out composites to
  // avoid accidental nesting (backend still guards for circular refs).
  const componentChoices = (materials || []).filter((m) => !m.is_composite && m.id !== editing.id);

  return (
    <div className="border border-cyan-200 bg-cyan-50/40 rounded-sm p-3 space-y-3" data-testid="bom-editor">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-cyan-700" />
          <div className="stamp-label text-cyan-800 text-[11px]">Bill of Materials · optional</div>
        </div>
        <div className="text-[10px] text-slate-500">
          Cost source: <span className="font-semibold text-slate-700">
            {override != null && override !== ""
              ? "Manual override"
              : bom.length > 0 ? "BOM aggregation" : "Fallback (unit price)"}
          </span>
        </div>
      </div>

      {bom.length === 0 && (
        <div className="text-[11px] text-slate-500 leading-relaxed bg-white/60 p-2 rounded-sm border border-dashed border-cyan-200">
          BOMs are optional. Without one, the composite falls back to the unit price below.
          Add components to derive the true cost, or pin a manual override to lock it.
        </div>
      )}

      {bom.map((row, idx) => {
        const comp = componentIndex[row.material_id];
        // Infer mode for legacy rows (qty_mode=null) the same way the backend
        // cost engine does — otherwise the editor shows "per piece" and then
        // silently SAVES "per piece", flipping the bundle's real pricing.
        const mode = row.qty_mode === "pack" || row.qty_mode === "unit"
          ? row.qty_mode
          : (comp && Number(comp.pack_size) > 0 ? "pack" : "unit");
        const rawUnitCost = comp ? Number(comp.unit_price || 0) : 0;
        const packSize = comp ? Number(comp.pack_size || 0) : 0;
        const packUnit = comp?.pack_unit || "";
        const canPack = mode === "pack" && packSize > 0;
        const effUnitCost = canPack ? rawUnitCost / packSize : rawUnitCost;
        const waste = comp ? Number(comp.waste_pct || 0) : 0;
        const lineCost = comp
          ? Number(row.qty_per_unit || 0) * effUnitCost * (1 + waste / 100)
          : 0;
        const packModeAvailable = comp && packSize > 0;
        return (
          <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-white p-2 rounded-sm border border-slate-200" data-testid={`bom-row-${idx}`}>
            <div className="col-span-12 sm:col-span-5">
              <label className="stamp-label text-[9px] text-slate-500">Component</label>
              <Select value={row.material_id || undefined} onValueChange={(v) => {
                // Iter-67 bug fix: when the picked raw has a pack_size,
                // default `qty_mode="pack"` so the composite's per-LM
                // cost comes out as (raw_price / pack_size), not the raw
                // unit price. Prior default of "unit" made every LM
                // composite silently expensive by the pack multiplier.
                const raw = (componentChoices || []).find((m) => m.id === v);
                const shouldDefaultPack = !!(raw && raw.pack_size && Number(raw.pack_size) > 0);
                updateRow(idx, {
                  material_id: v,
                  ...(row.qty_mode == null && shouldDefaultPack ? { qty_mode: "pack" } : {}),
                });
              }}>
                <SelectTrigger data-testid={`bom-material-${idx}`} className="rounded-sm h-9"><SelectValue placeholder="Pick a raw material..." /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {componentChoices.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="text-xs">{m.name}</span>
                      <span className="text-[10px] text-slate-400 ml-2">${Number(m.unit_price || 0).toFixed(2)}/{m.unit}</span>
                      {Number(m.pack_size) > 0 && (
                        <span className="text-[10px] text-cyan-600 ml-1">· {Number(m.pack_size)}{m.pack_unit || "m"}/pack</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-6 sm:col-span-2">
              <label className="stamp-label text-[9px] text-slate-500">Qty / unit</label>
              <Input
                data-testid={`bom-qty-${idx}`}
                type="number"
                step="0.01"
                value={row.qty_per_unit}
                onChange={(e) => updateRow(idx, { qty_per_unit: e.target.value === "" ? 0 : Number(e.target.value) })}
                className="rounded-sm h-9 font-mono text-sm"
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <label className="stamp-label text-[9px] text-slate-500">Mode</label>
              <Select
                value={mode}
                onValueChange={(v) => updateRow(idx, { qty_mode: v })}
              >
                <SelectTrigger data-testid={`bom-mode-${idx}`} className="rounded-sm h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unit">
                    <span className="text-xs">per {comp?.unit || "unit"}</span>
                  </SelectItem>
                  <SelectItem value="pack" disabled={!packModeAvailable}>
                    <span className="text-xs">per {packUnit || "m"} (pack)</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-10 sm:col-span-2 text-right">
              <label className="stamp-label text-[9px] text-slate-500 block">Line cost</label>
              <div className="h-9 flex flex-col items-end justify-center font-mono text-sm text-slate-900">
                {comp ? fmtCurrencyExact(lineCost) : <span className="text-[10px] text-slate-400">pick component</span>}
                {comp && canPack && (
                  <span className="text-[9px] text-cyan-600 font-normal leading-none" data-testid={`bom-conv-${idx}`}>
                    ${effUnitCost.toFixed(2)}/{packUnit || "m"}
                  </span>
                )}
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1 flex items-end justify-end">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                onClick={() => removeRow(idx)}
                data-testid={`bom-remove-${idx}`}
              >
                <Trash2 size={13} />
              </Button>
            </div>
            {comp && mode === "pack" && !packModeAvailable && (
              <div className="col-span-12 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
                <b>"{comp.name}"</b> has no pack size set — open it and add pack_size + pack_unit to enable per-pack pricing.
              </div>
            )}
            {row.material_id && (
              <div className="col-span-12">
                <Input
                  placeholder="Notes (optional)"
                  value={row.notes || ""}
                  onChange={(e) => updateRow(idx, { notes: e.target.value })}
                  className="rounded-sm h-8 text-xs"
                />
              </div>
            )}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          onClick={addRow}
          className="rounded-sm h-9 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
          data-testid="bom-add-row"
        >
          <Plus size={13} className="mr-1" /> Add component
        </Button>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="stamp-label text-[9px] text-slate-500">Derived from BOM</div>
            <div className="font-mono text-sm font-semibold text-slate-900" data-testid="bom-derived-cost">{fmtCurrencyExact(derivedCost)}</div>
          </div>
          <div className="text-right border-l border-slate-200 pl-4">
            <div className="stamp-label text-[9px] text-cyan-700">Effective cost / unit</div>
            <div className="font-mono text-base font-bold text-cyan-800" data-testid="bom-effective-cost">{fmtCurrencyExact(effectiveCost)}</div>
          </div>
        </div>
      </div>

      <div className="border-t border-cyan-200 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="stamp-label text-[10px] text-slate-500">Manual override (locks the composite cost)</label>
          <div className="flex items-center gap-1.5">
            <Input
              data-testid="bom-override-input"
              type="number"
              step="0.01"
              placeholder="Leave blank to use BOM/fallback"
              value={override == null ? "" : override}
              onChange={(e) => setEditing({ ...editing, composite_cost_override: e.target.value === "" ? null : Number(e.target.value) })}
              className="rounded-sm h-9 font-mono text-sm flex-1"
            />
            {override != null && override !== "" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing({ ...editing, composite_cost_override: null })}
                className="h-9 w-9 p-0 text-slate-500"
                data-testid="bom-override-clear"
                title="Clear override"
              >
                <X size={13} />
              </Button>
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">Overrides BOM aggregation when set.</div>
        </div>
        <div className="text-[11px] text-slate-600 flex items-start gap-1.5 bg-white/70 p-2 rounded-sm border border-cyan-100">
          <Calculator size={12} className="text-cyan-700 mt-0.5 shrink-0" />
          <span>Labour is a <b>separate line</b> on quotes — never baked into a composite's cost. GP margin is applied once at the quote total.</span>
        </div>
      </div>
    </div>
  );
}

export default function Materials() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [fenceFilter, setFenceFilter] = useState("all");
  const [tab, setTab] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data: materials = [] } = useQuery({
    queryKey: ["materials", search, fenceFilter],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (fenceFilter !== "all") params.fence_type = fenceFilter;
      return (await api.get("/materials", { params })).data;
    },
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers-all"], queryFn: async () => (await api.get("/suppliers")).data,
  });
  // Unfiltered list for the BOM component picker — must NOT be scoped by the
  // page search/fence filter, otherwise the picker only shows matching raws.
  const { data: allMaterialsForBom = [] } = useQuery({
    queryKey: ["materials-all-for-bom"],
    queryFn: async () => (await api.get("/materials")).data,
    enabled: open,
  });
  const { data: fenceTypes = [] } = useQuery({
    queryKey: ["fence-types"], queryFn: async () => (await api.get("/fence-types")).data,
  });
  const supMap = Object.fromEntries(suppliers.map((s) => [s.id, s.name]));

  // Client-side tab filter — the server returns everything and we slice by
  // tab here so switching tabs is instant (no round-trip).
  const filteredMaterials = useMemo(() => {
    return materials.filter((m) => {
      if (tab === "all") return true;
      if (tab === "composite") return !!m.is_composite;
      if (tab === "review") return !!m.needs_cost_review;
      // Raw material = non-composite in the "material" category
      if (tab === "material") return !m.is_composite && m.category === "material";
      return m.category === tab;   // labour, incidental
    });
  }, [materials, tab]);

  // Counts drive the badges next to each tab label
  const counts = useMemo(() => ({
    all: materials.length,
    material: materials.filter((m) => !m.is_composite && m.category === "material").length,
    composite: materials.filter((m) => m.is_composite).length,
    labour: materials.filter((m) => m.category === "labour").length,
    incidental: materials.filter((m) => m.category === "incidental").length,
    review: materials.filter((m) => m.needs_cost_review).length,
  }), [materials]);

  const save = useMutation({
    mutationFn: async (body) => {
      const payload = {
        ...body,
        supplier_id: body.supplier_id === "none" ? null : body.supplier_id,
        unit_price: parseFloat(body.unit_price) || 0,
        // iter-63: pack info — coerce blanks to null so backend clears them
        pack_size: body.pack_size === "" || body.pack_size == null ? null : Number(body.pack_size),
        pack_unit: body.pack_unit || null,
        waste_pct: body.waste_pct === "" || body.waste_pct == null ? null : Number(body.waste_pct),
      };
      // Composite-only fields — only send when relevant, otherwise omit
      if (!payload.is_composite) {
        payload.composite_bom = [];
        payload.composite_cost_override = null;
      } else {
        // Strip empty BOM rows before saving
        payload.composite_bom = (payload.composite_bom || [])
          .filter((r) => r && r.material_id && Number(r.qty_per_unit) > 0)
          .map((r) => ({
            material_id: r.material_id,
            qty_per_unit: Number(r.qty_per_unit),
            // Preserve null for legacy rows so the backend keeps inferring
            // "pack" from the raw's pack_size — never coerce null to "unit".
            qty_mode: r.qty_mode === "pack" || r.qty_mode === "unit" ? r.qty_mode : null,
            notes: r.notes || null,
          }));
        if (payload.composite_cost_override === "" || payload.composite_cost_override === undefined) {
          payload.composite_cost_override = null;
        }
      }
      if (body.id) return (await api.patch(`/materials/${body.id}`, payload)).data;
      return (await api.post("/materials", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ predicate: (q) => q.queryKey?.[0]?.toString().startsWith("materials") }); setOpen(false); toast.success("Saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save material"),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/materials/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ predicate: (q) => q.queryKey?.[0]?.toString().startsWith("materials") }); toast.success("Deleted"); },
  });
  const markReviewed = useMutation({
    mutationFn: async (id) => (await api.post(`/materials/${id}/mark-cost-reviewed`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["materials"] }); toast.success("Cost marked as reviewed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not mark reviewed"),
  });
  // iter-63: one-click backfill of pack_size/pack_unit for raw materials that
  // look like panels/rolls, using the site-wide ratios card as the default.
  const backfill = useMutation({
    mutationFn: async () => (await api.post("/materials/backfill-pack-sizes")).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["materials"] });
      const n = res?.count || 0;
      toast.success(n === 0
        ? "No raw materials needed backfilling"
        : `Backfilled pack size on ${n} raw material${n === 1 ? "" : "s"}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Backfill failed"),
  });

  const toggleFenceType = (ft) => {
    const has = (editing.fence_types || []).includes(ft);
    setEditing({ ...editing, fence_types: has ? editing.fence_types.filter((t) => t !== ft) : [...(editing.fence_types || []), ft] });
  };

  const openNew = () => {
    // If the current tab is a category, pre-seed the new material with that
    // category / is_composite flag so admins can add-into-tab in one click.
    const seed = { ...EMPTY };
    if (tab === "composite") seed.is_composite = true;
    else if (tab === "labour" || tab === "incidental") seed.category = tab;
    setEditing(seed);
    setOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Materials"
        subtitle="Cost prices for materials, composites, labour & incidentals — GP margin applies once at the quote total."
        breadcrumb={["CRM", "Materials"]}
      />
      <div className="px-6 lg:px-10 py-6">
        <PricingRatiosCard />
        <FencePhotoManager />

        {counts.review > 0 && (
          <Card className="rounded-sm border-amber-300 bg-amber-50/70 mb-4 p-3 flex items-start gap-3" data-testid="cost-review-banner">
            <div className="w-8 h-8 shrink-0 bg-amber-200 text-amber-800 flex items-center justify-center rounded-sm">
              <AlertTriangle size={14} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-semibold text-amber-900">{counts.review} composite{counts.review === 1 ? "" : "s"} need cost review</div>
              <div className="text-[11px] text-amber-800 leading-relaxed">
                Migrated from the retired Pricing DB — the shown price is the old sell rate, not a validated cost. Open each one, add a BOM or set a real cost, then Mark cost reviewed.
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTab("review")}
              className="rounded-sm h-8 border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
              data-testid="review-filter-btn"
            >
              Show list
            </Button>
          </Card>
        )}

        <Card className="rounded-sm border-slate-200 industrial-shadow">
          <div className="p-3 sm:p-4 border-b border-slate-200 bg-slate-50/50 space-y-3">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="w-full overflow-x-auto flex justify-start bg-slate-100/70 rounded-sm h-auto p-1">
                {TAB_DEFS.map((t) => (
                  <TabsTrigger
                    key={t.key}
                    value={t.key}
                    data-testid={t.testid}
                    className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-cyan-700 data-[state=active]:shadow-sm text-xs px-3 h-8"
                  >
                    {t.label}
                    {counts[t.key] > 0 && (
                      <span className={`ml-1.5 rounded-full px-1.5 text-[10px] font-mono ${t.key === "review" ? "bg-amber-200 text-amber-800" : "bg-slate-200 text-slate-700"}`}>{counts[t.key]}</span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1 max-w-md">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="material-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search materials..." className="pl-9 h-10 rounded-sm" />
              </div>
              <Select value={fenceFilter} onValueChange={setFenceFilter}>
                <SelectTrigger data-testid="material-fence-filter" className="w-full sm:w-48 h-10 rounded-sm"><SelectValue placeholder="Fence type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All fence types</SelectItem>
                  {fenceTypes.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {/* iter-63: primary "Add material" CTA lives inside the filter
                  band so it sits next to the tab/search context users are
                  already in. */}
              <Button
                variant="outline"
                onClick={() => {
                  if (window.confirm("Backfill pack_size / pack_unit on Colorbond, chain-wire and stock-bar raw materials from the ratios card? Existing values are preserved.")) {
                    backfill.mutate();
                  }
                }}
                disabled={backfill.isPending}
                className="rounded-sm h-10 border-cyan-300 text-cyan-700 hover:bg-cyan-50 sm:ml-auto shrink-0"
                data-testid="backfill-packs-btn"
                title="Seed pack sizes from the ratios card"
              >
                <Package size={14} className="mr-2" /> Backfill packs
              </Button>
              <Button
                data-testid="new-material-btn"
                onClick={openNew}
                className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white shrink-0"
              >
                <Plus size={16} className="mr-2" /> Add material
              </Button>
            </div>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden divide-y divide-slate-100" data-testid="materials-mobile-list">
            {filteredMaterials.map((m) => (
              <div key={m.id} className="p-3 flex items-start gap-3" data-testid={`material-card-${m.id}`}>
                <div className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-sm ${m.is_composite ? "bg-cyan-100 text-cyan-700" : "bg-slate-100 text-slate-700"}`}>
                  {m.is_composite ? <Layers size={14} /> : <Package size={14} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 text-sm truncate">{m.name}</div>
                      {m.sku && <div className="font-mono text-[10px] text-slate-400 truncate">{m.sku}</div>}
                    </div>
                    <div className="font-mono font-semibold text-slate-900 text-sm shrink-0 text-right" data-testid={`material-cost-${m.id}`}>
                      {fmtCurrencyExact(m.effective_cost != null ? m.effective_cost : m.unit_price)}
                      {m.is_composite && (
                        <div className="stamp-label text-[8px] text-slate-400 font-normal mt-0.5">
                          {m.effective_cost_source === "override" ? "· manual override"
                            : m.effective_cost_source === "bom" ? "· from BOM"
                            : "· fallback"}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {m.is_composite && <Badge variant="outline" className="rounded-sm text-[9px] border-cyan-300 bg-cyan-50 text-cyan-700 h-4 px-1">BUNDLE</Badge>}
                    {m.needs_cost_review && <Badge variant="outline" className="rounded-sm text-[9px] border-amber-300 bg-amber-50 text-amber-700 h-4 px-1" data-testid={`review-badge-${m.id}`}>REVIEW</Badge>}
                    <Badge variant="outline" className={`rounded-sm text-[10px] ${m.category === "labour" ? "border-amber-300 bg-amber-50 text-amber-800" : m.category === "incidental" ? "border-sky-300 bg-sky-50 text-sky-800" : "border-slate-300"}`}>
                      {m.category}
                    </Badge>
                    <span className="font-mono text-[10px] text-slate-500">per {m.unit}</span>
                    {supMap[m.supplier_id] && (
                      <span className="text-[10px] text-slate-500 truncate">· {supMap[m.supplier_id]}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditing({ ...m, supplier_id: m.supplier_id || "none", composite_bom: m.composite_bom || [] }); setOpen(true); }} data-testid={`edit-material-mobile-${m.id}`}><Pencil size={13} /></Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => del.mutate(m.id)} data-testid={`del-material-mobile-${m.id}`}><Trash2 size={13} /></Button>
                </div>
              </div>
            ))}
            {filteredMaterials.length === 0 && <div className="px-4 py-12 text-center text-slate-400 text-sm">No materials in this view</div>}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left stamp-label text-slate-500">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 hidden md:table-cell">Fence types</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Supplier</th>
                  <th className="px-4 py-3">Unit</th>
                  <th className="px-4 py-3 text-right">Cost</th>
                  <th className="px-4 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMaterials.map((m) => (
                  <tr key={m.id} className={`hover:bg-cyan-50/30 ${m.needs_cost_review ? "bg-amber-50/30" : ""}`} data-testid={`material-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-sm ${m.is_composite ? "bg-cyan-100 text-cyan-700" : "bg-slate-100 text-slate-700"}`}>
                          {m.is_composite ? <Layers size={13} /> : <Package size={13} />}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <div className="font-medium text-slate-900">{m.name}</div>
                            {m.is_composite && <Badge variant="outline" className="rounded-sm text-[9px] border-cyan-300 bg-cyan-50 text-cyan-700 h-4 px-1">BUNDLE</Badge>}
                            {m.needs_cost_review && <Badge variant="outline" className="rounded-sm text-[9px] border-amber-300 bg-amber-50 text-amber-700 h-4 px-1" data-testid={`review-badge-${m.id}`}>REVIEW</Badge>}
                          </div>
                          {m.sku && <div className="font-mono text-[10px] text-slate-400">{m.sku}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`rounded-sm text-[10px] ${m.category === "labour" ? "border-amber-300 bg-amber-50 text-amber-800" : m.category === "incidental" ? "border-sky-300 bg-sky-50 text-sky-800" : "border-slate-300"}`}>
                        {m.category}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(m.fence_types || []).slice(0, 3).map((ft) => (
                          <Badge key={ft} variant="outline" className="rounded-sm text-[10px] border-slate-200 text-slate-600">{FENCE_LABELS[ft] || ft}</Badge>
                        ))}
                        {m.fence_types?.length > 3 && <span className="text-[10px] text-slate-400">+{m.fence_types.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden lg:table-cell text-xs">{supMap[m.supplier_id] || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{m.unit}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900" data-testid={`material-cost-${m.id}`}>
                      {fmtCurrencyExact(m.effective_cost != null ? m.effective_cost : m.unit_price)}
                      {m.is_composite && (
                        <div className="stamp-label text-[9px] text-slate-400 font-normal mt-0.5">
                          {m.effective_cost_source === "override" ? "manual override"
                            : m.effective_cost_source === "bom" ? "from BOM"
                            : "fallback"}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {m.needs_cost_review && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => markReviewed.mutate(m.id)}
                            title="Mark cost as reviewed"
                            data-testid={`mark-reviewed-${m.id}`}
                          >
                            <CheckCircle2 size={12} />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing({ ...m, supplier_id: m.supplier_id || "none", composite_bom: m.composite_bom || [] }); setOpen(true); }} data-testid={`edit-material-${m.id}`}><Pencil size={12} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => del.mutate(m.id)}><Trash2 size={12} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredMaterials.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No materials in this view</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="material-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              {editing?.id ? "Edit material" : "Add material"}
              {editing?.is_composite && <Badge variant="outline" className="rounded-sm text-[10px] border-cyan-300 bg-cyan-50 text-cyan-700">BUNDLE</Badge>}
              {editing?.needs_cost_review && <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 bg-amber-50 text-amber-700">NEEDS REVIEW</Badge>}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name" required><Input data-testid="material-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
                <Field label="SKU"><Input value={editing.sku || ""} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} /></Field>
                <Field label="Category">
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="material-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="material">Material</SelectItem>
                      <SelectItem value="labour">Labour</SelectItem>
                      <SelectItem value="incidental">Incidental</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Unit">
                  <Select value={editing.unit} onValueChange={(v) => setEditing({ ...editing, unit: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={editing.is_composite ? "Fallback cost / unit (AUD)" : "Cost / unit (AUD)"} required>
                  <Input data-testid="material-price-input" type="number" step="0.01" value={editing.unit_price} onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })} />
                </Field>
                <Field label="Supplier">
                  <Select value={editing.supplier_id || "none"} onValueChange={(v) => setEditing({ ...editing, supplier_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {/* iter-63 — Supplier pack info: shown for raw materials so
                  panels/rolls can be linked to a linear-metre yield. Composite
                  bundles derive their cost from BOM + pack conversion, so
                  they don't need their own pack info. */}
              {!editing.is_composite && editing.category === "material" && (
                <div className="border border-cyan-100 bg-cyan-50/40 rounded-sm p-3 space-y-2" data-testid="pack-info-section">
                  <div className="flex items-center gap-2">
                    <Package size={13} className="text-cyan-700" />
                    <div className="stamp-label text-cyan-800 text-[11px]">Supplier pack · optional</div>
                    <div className="text-[10px] text-slate-500 leading-tight">
                      How much finished fence one bought unit yields. E.g. Colorbond panel — 2.4 m; chain-wire roll — 25 m.
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label={`Pack size (per ${editing.unit || "unit"})`}>
                      <Input
                        data-testid="material-pack-size"
                        type="number"
                        step="0.01"
                        placeholder="e.g. 2.4"
                        value={editing.pack_size == null ? "" : editing.pack_size}
                        onChange={(e) => setEditing({ ...editing, pack_size: e.target.value === "" ? null : Number(e.target.value) })}
                      />
                    </Field>
                    <Field label="Pack unit">
                      <Select
                        value={editing.pack_unit || "__none"}
                        onValueChange={(v) => setEditing({ ...editing, pack_unit: v === "__none" ? "" : v })}
                      >
                        <SelectTrigger data-testid="material-pack-unit"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">— None —</SelectItem>
                          {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Waste % (optional)">
                      <Input
                        data-testid="material-waste-pct"
                        type="number"
                        step="0.5"
                        placeholder="e.g. 5"
                        value={editing.waste_pct == null ? "" : editing.waste_pct}
                        onChange={(e) => setEditing({ ...editing, waste_pct: e.target.value === "" ? null : Number(e.target.value) })}
                      />
                    </Field>
                  </div>
                  {Number(editing.pack_size) > 0 && editing.pack_unit && Number(editing.unit_price) > 0 && (
                    <div className="text-[11px] text-cyan-700 bg-white/70 border border-cyan-100 rounded-sm px-2 py-1 flex items-center gap-1.5" data-testid="pack-conversion-preview">
                      <Calculator size={11} />
                      Effective:
                      <span className="font-mono font-semibold">
                        ${(Number(editing.unit_price) / Number(editing.pack_size)).toFixed(2)}
                        /{editing.pack_unit}
                      </span>
                      <span className="text-slate-500">— composite BOM rows in "per {editing.pack_unit} (pack)" mode use this rate.</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-start justify-between gap-3 border border-slate-200 rounded-sm p-3 bg-slate-50/50">
                <div className="flex items-start gap-2.5 min-w-0">
                  <Layers size={16} className={`mt-0.5 ${editing.is_composite ? "text-cyan-700" : "text-slate-400"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Composite bundle</div>
                    <div className="text-[11px] text-slate-500 leading-tight">
                      Turn on for ready-to-quote fence bundles (e.g. "Colorbond 1.8m LM"). Add optional BOM to derive cost from components.
                    </div>
                  </div>
                </div>
                <Switch
                  data-testid="is-composite-toggle"
                  checked={!!editing.is_composite}
                  onCheckedChange={(v) => setEditing({ ...editing, is_composite: v })}
                />
              </div>

              {editing.is_composite && (
                <BomEditor editing={editing} setEditing={setEditing} materials={allMaterialsForBom} />
              )}

              {editing.needs_cost_review && editing.id && (
                <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 flex items-center justify-between gap-2" data-testid="review-panel">
                  <div className="flex items-start gap-2 min-w-0">
                    <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                    <div className="text-[11px] text-amber-900 leading-relaxed">
                      This composite's price came from the retired Pricing DB and is an old <b>sell rate</b>, not a validated cost. Populate the BOM or set a manual override, then mark reviewed.
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { markReviewed.mutate(editing.id); setEditing({ ...editing, needs_cost_review: false }); }}
                    className="rounded-sm h-8 border-emerald-400 text-emerald-800 hover:bg-emerald-50 shrink-0"
                    data-testid="mark-reviewed-dialog"
                  >
                    <CheckCircle2 size={13} className="mr-1" /> Mark reviewed
                  </Button>
                </div>
              )}

              <Field label="Applicable fence types">
                <div className="grid grid-cols-3 gap-1.5">
                  {fenceTypes.map((ft) => {
                    const on = (editing.fence_types || []).includes(ft.value);
                    return (
                      <button
                        key={ft.value}
                        type="button"
                        onClick={() => toggleFenceType(ft.value)}
                        data-testid={`fence-type-${ft.value}`}
                        className={`px-2.5 py-1.5 text-xs border rounded-sm text-left transition-colors ${on ? "bg-cyan-600 text-white border-cyan-600" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
                      >
                        {ft.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Description"><Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-material-btn" onClick={() => save.mutate(editing)} disabled={save.isPending} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save material</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
